import type { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import {
  drawBalance,
  drawCycleStones,
  drawGem,
  drawBulb,
  drawGoldText,
  drawPlaque,
  drawTabletFace,
  inRect,
  roundedRect,
  type Align,
  type BalanceSegment,
  type GemKind,
  type Rect,
} from './tabletCanvas';

export type TabletItem =
  | { kind: 'image'; image: CanvasImageSource; rect: Rect }
  | {
      kind: 'text';
      text: string;
      x: number;
      y: number;
      size: number;
      align?: Align;
      weight?: number;
      alpha?: number;
      /** Leaning letters: the tablet uses them for a tip, never for a name. */
      italic?: boolean;
      /** Pixels the line may take before it is squeezed to fit. */
      maxWidth?: number;
    }
  | { kind: 'plaque'; rect: Rect; text: string; size: number }
  | { kind: 'stones'; x: number; y: number; r: number; colors: readonly string[]; phase: number }
  | {
      kind: 'bulb';
      x: number;
      y: number;
      r: number;
      text: string;
      sub: string;
      lit: boolean;
      scale: number;
    }
  | {
      kind: 'balance';
      rect: Rect;
      segments: readonly BalanceSegment[];
      fontSize: number;
      /** A needle at this fraction of the bar: a Card clash's roll. */
      needle?: number;
    }
  | {
      kind: 'gem';
      id: string;
      rect: Rect;
      gem: GemKind;
      label: string;
      enabled: boolean;
      active?: boolean;
      fontSize?: number;
      flat?: boolean;
      /** Spell points this costs, drawn as a badge beside the name. */
      cost?: number;
      onTap?: () => void;
    };

/** A gem a tap, a laser or a pad's cursor can press: every one but a flat lamp, which only shows a level. */
export function isButton(item: TabletItem): item is Extract<TabletItem, { kind: 'gem' }> {
  return item.kind === 'gem' && !item.flat;
}

export interface TabletOptions {
  name: string;
  /** Metres. */
  width: number;
  height: number;
  /** Texture pixels. */
  texWidth: number;
  texHeight: number;
  position: Vector3;
  /** Radians the far edge is raised by, so the face leans towards the player. */
  tilt: number;
  parent: TransformNode | null;
  /** Screwed to the table (default) or hanging in the air, gently bobbing. */
  floating?: boolean;
  /** Called for every tap that lands on a gem, before its action runs; `enabled` false is a dead gem. */
  onGemTap?: (gem: { id: string; enabled: boolean }) => void;
}

const BODY_THICKNESS = 0.012;
const PRESS_SECONDS = 0.25;
/** Redraws a second while a pressed gem springs back, whatever the sparkle's rate. */
const PRESS_HZ = 12;

/**
 * Whether two lists of items draw the same face. Taps are left out, since a rebuild makes fresh
 * ones every time, and pictures are the same only when they are the same picture.
 */
export function sameFace(a: readonly TabletItem[], b: readonly TabletItem[]): boolean {
  return a.length === b.length && a.every((item, i) => samePlain(item, b[i]));
}

function samePlain(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'function' && typeof b === 'function') return true;
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((x, i) => samePlain(x, b[i]));
  if (!isPlain(a) || !isPlain(b)) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => samePlain(a[key], b[key]));
}

function isPlain(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * How often a face redraws with nothing new on it: while a gem springs back from a press, and at
 * `hz` while enabled gems sparkle, `hz` 0 meaning they keep still. Otherwise never.
 */
export function idleRedrawHz(pressing: boolean, sparkling: boolean, hz: number): number {
  if (pressing) return Math.max(hz, PRESS_HZ);
  return sparkling ? hz : 0;
}

/**
 * A wooden tablet lying on the table with its face drawn on a canvas: gold text, plaques and gem
 * buttons. Taps arrive as scene picks and are matched to gems through the hit's texture
 * coordinates, which works the same for a mouse, a finger and a VR pointer.
 */
export class Tablet {
  readonly root: TransformNode;
  readonly face: Mesh;
  readonly body: Mesh;
  private readonly texture: DynamicTexture;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly backdrop: HTMLCanvasElement;
  private items: TabletItem[] = [];
  private readonly pressed = new Map<string, number>();
  /** The gem a gamepad cursor rests on, drawn with a gold frame. */
  private focused: string | null = null;
  private dirty = true;
  private backdropDirty = true;
  private lastDraw = -1;
  private wood: CanvasImageSource | null = null;
  private readonly restY: number;

  constructor(
    private readonly scene: Scene,
    private readonly options: TabletOptions,
  ) {
    const { name, width, height, texWidth, texHeight } = options;
    this.root = new TransformNode(`${name}Tablet`, scene);
    this.root.parent = options.parent;
    this.root.position.copyFrom(options.position);
    this.root.rotation.x = options.tilt;
    this.restY = options.position.y;

    this.body = MeshBuilder.CreateBox(
      `${name}TabletBody`,
      { width, height: BODY_THICKNESS, depth: height },
      scene,
    );
    this.body.parent = this.root;
    this.body.position.y = -BODY_THICKNESS / 2;
    this.body.isPickable = false;
    this.body.receiveShadows = true;
    const bodyMaterial = new StandardMaterial(`${name}TabletWood`, scene);
    const grain = new Texture('/textures/wood/walnut_color.jpg', scene);
    grain.uScale = 1.2;
    grain.vScale = 0.8;
    bodyMaterial.diffuseTexture = grain;
    bodyMaterial.diffuseColor = new Color3(1.15, 0.78, 0.45);
    bodyMaterial.specularColor = new Color3(0.2, 0.16, 0.1);
    this.body.material = bodyMaterial;

    this.face = createFaceQuad(scene, `${name}TabletFace`, width, height);
    this.face.parent = this.root;
    this.face.position.y = 0.0006;
    this.face.isPickable = true;
    this.face.metadata = { tablet: name };

    this.texture = new DynamicTexture(
      `${name}TabletCanvas`,
      { width: texWidth, height: texHeight },
      scene,
      true,
    );
    this.texture.anisotropicFilteringLevel = 8;
    this.ctx = this.texture.getContext() as CanvasRenderingContext2D;
    this.backdrop = document.createElement('canvas');
    this.backdrop.width = texWidth;
    this.backdrop.height = texHeight;
    const material = new StandardMaterial(`${name}TabletFaceMaterial`, scene);
    material.diffuseTexture = this.texture;
    material.emissiveTexture = this.texture;
    material.emissiveColor = new Color3(0.28, 0.28, 0.28);
    material.specularColor = new Color3(0.05, 0.04, 0.03);
    material.specularPower = 24;
    this.face.material = material;
  }

  /** The walnut photo behind everything; until it loads the face is plain bronze. */
  setWood(image: CanvasImageSource | null): void {
    this.wood = image;
    this.backdropDirty = true;
    this.dirty = true;
  }

  /**
   * The face's items; it is drawn again only when they draw differently. A rebuild on every session
   * and shell notice used to repaint and re-upload all three faces, changed or not.
   */
  setItems(items: TabletItem[]): void {
    if (!sameFace(this.items, items)) this.dirty = true;
    // The new items carry the current taps, even when they draw the same.
    this.items = items;
  }

  /** Everything written on the tablet, for tests. */
  text(): string {
    return this.items
      .map((item) =>
        item.kind === 'text' || item.kind === 'plaque'
          ? item.text
          : item.kind === 'gem'
            ? item.label
            : item.kind === 'balance'
              ? item.segments.map((s) => `${s.label} ${Math.round(100 * s.share)} %`).join(' · ')
              : '',
      )
      .filter(Boolean)
      .join(' · ');
  }

  /** Redraws when the content changed, and at `hz` while gems sparkle (0: they keep still). */
  update(time: number, hz: number): void {
    if (this.options.floating) this.root.position.y = this.restY + 0.006 * Math.sin(time * 0.9);
    const sparkling = this.items.some((i) => i.kind === 'gem' && i.enabled);
    const rate = idleRedrawHz(this.pressed.size > 0, sparkling, hz);
    const due = rate > 0 && (this.lastDraw < 0 || time - this.lastDraw >= 1 / rate);
    if (!this.dirty && !due) return;
    if (!this.root.isEnabled()) return;
    this.draw(time);
  }

  /** Runs the gem under a pick on this tablet's face. */
  tap(pick: PickingInfo, time: number): boolean {
    if (pick.pickedMesh !== this.face) return false;
    const uv = pick.getTextureCoordinates();
    if (!uv) return true;
    const x = uv.x * this.options.texWidth;
    const y = (1 - uv.y) * this.options.texHeight;
    for (const item of this.items) {
      if (!isButton(item) || !inRect(item.rect, x, y)) continue;
      this.pressGem(item, time);
      return true;
    }
    return true;
  }

  /**
   * The gems a cursor could rest on, in the order they are drawn. A flat lamp only shows a level,
   * so it is no stop for a pad's cursor and no button (client-tablets-ui:bugs#8).
   */
  gems(): { id: string; rect: Rect; enabled: boolean }[] {
    return this.items.flatMap((item) =>
      isButton(item) ? [{ id: item.id, rect: item.rect, enabled: item.enabled }] : [],
    );
  }

  /** Presses a gem by id, as a tap on it would; false when there is no such gem. */
  press(id: string, time: number): boolean {
    const item = this.items.find((i) => isButton(i) && i.id === id);
    if (!item || !isButton(item)) return false;
    this.pressGem(item, time);
    return true;
  }

  /** Frames a gem in gold for the gamepad, or nothing. */
  setFocus(id: string | null): void {
    if (id === this.focused) return;
    this.focused = id;
    this.dirty = true;
  }

  private pressGem(item: Extract<TabletItem, { kind: 'gem' }>, time: number): void {
    this.options.onGemTap?.({ id: item.id, enabled: item.enabled });
    if (!item.enabled) return;
    this.pressed.set(item.id, time);
    this.dirty = true;
    item.onTap?.();
  }

  dispose(): void {
    this.texture.dispose();
    this.face.material?.dispose();
    // With its walnut texture, which every new game used to leave behind.
    this.body.material?.dispose(false, true);
    this.face.dispose();
    this.body.dispose();
    this.root.dispose();
  }

  private draw(time: number): void {
    const { texWidth: w, texHeight: h } = this.options;
    if (this.backdropDirty) {
      const bctx = this.backdrop.getContext('2d')!;
      bctx.clearRect(0, 0, w, h);
      drawTabletFace(bctx, w, h, this.wood, !this.options.floating);
      this.backdropDirty = false;
    }
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(this.backdrop, 0, 0);
    for (const item of this.items) {
      switch (item.kind) {
        case 'image':
          ctx.drawImage(item.image, item.rect.x, item.rect.y, item.rect.w, item.rect.h);
          break;
        case 'text':
          drawGoldText(
            ctx,
            item.text,
            item.x,
            item.y,
            item.size,
            item.align,
            item.weight,
            item.alpha,
            undefined,
            item.maxWidth,
            false,
            item.italic,
          );
          break;
        case 'plaque':
          drawPlaque(ctx, item.rect, item.text, item.size);
          break;
        case 'stones':
          drawCycleStones(ctx, item.x, item.y, item.r, item.colors, item.phase);
          break;
        case 'balance':
          drawBalance(ctx, item.rect, item.segments, item.fontSize, item.needle);
          break;
        case 'bulb':
          drawBulb(ctx, item.x, item.y, item.r, item.text, item.sub, item.lit, item.scale);
          break;
        case 'gem': {
          const since = this.pressed.get(item.id);
          const pressed = since === undefined ? 0 : Math.max(0, 1 - (time - since) / PRESS_SECONDS);
          if (since !== undefined && pressed === 0) this.pressed.delete(item.id);
          drawGem(ctx, item.rect, item.gem, {
            label: item.label,
            enabled: item.enabled,
            active: item.active,
            pressed,
            time,
            seed: hashSeed(item.id),
            fontSize: item.fontSize,
            flat: item.flat,
            cost: item.cost,
          });
          if (item.id === this.focused) drawFocusFrame(ctx, item.rect);
          break;
        }
      }
    }
    this.texture.update(true);
    this.dirty = false;
    this.lastDraw = time;
  }
}

/**
 * A quad lying in the XZ plane, facing up, whose texture reads correctly for a player standing on
 * the +z side: canvas left is the player's left (+x) and the canvas top is the far edge.
 */
function createFaceQuad(scene: Scene, name: string, width: number, height: number): Mesh {
  const hw = width / 2;
  const hh = height / 2;
  const mesh = new Mesh(name, scene);
  const data = new VertexData();
  // A far-left, B far-right, C near-right, D near-left (from the player's point of view).
  data.positions = [hw, 0, -hh, -hw, 0, -hh, -hw, 0, hh, hw, 0, hh];
  data.normals = [0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0];
  data.uvs = [0, 1, 1, 1, 1, 0, 0, 0];
  data.indices = [0, 2, 1, 0, 3, 2];
  data.applyToMesh(mesh, false);
  return mesh;
}

/** The gamepad cursor on a gem: a gold frame just outside its edge, the same on every tablet. */
function drawFocusFrame(ctx: CanvasRenderingContext2D, rect: Rect): void {
  const pad = 8;
  ctx.save();
  roundedRect(
    ctx,
    { x: rect.x - pad, y: rect.y - pad, w: rect.w + 2 * pad, h: rect.h + 2 * pad },
    Math.min(18, rect.h / 2 + pad),
  );
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(255, 224, 140, 0.95)';
  ctx.shadowColor = 'rgba(255, 200, 90, 0.8)';
  ctx.shadowBlur = 12;
  ctx.stroke();
  ctx.restore();
}

function hashSeed(id: string): number {
  let n = 0;
  for (let i = 0; i < id.length; i++) n = (n * 31 + id.charCodeAt(i)) % 1000;
  return n / 100;
}
